import React, { useRef, useEffect, useState, useContext, useMemo } from "react";
import {
  Selection,
  Inject,
  Filter,
  Page,
  Edit,
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  SortSettings,
} from "@syncfusion/ej2-react-grids";
import {
  ExcelExport,
  PdfExport,
  Group,
  Toolbar,
  Search,
  Sort,
} from "@syncfusion/ej2-react-grids";
import devProgessTemplate from "./grid_templates/devProgressTemp";
// import devTypeTemplate from "./grid_templates/typesTemplate";
import resumeDwload from "./grid_templates/resumeDwload";
import devDocs from "./grid_templates/devDocsTemplate.js";
import statusTemplate from "./grid_templates/statusTemplate";
import MigrateDevs from "./pages/MigrateDevs/MigrateDevs";
import AllResourcesFilterBar from "./AllResources/AllResourcesFilterBar";
import { colRef, docRef } from "./globals/services";
import MigrateDevOverlay from "./pages/MigrateDevs/MigrateDevOverlay";
import { GlobalVendorContex } from "./Firebase";
import { useNavigate } from "react-router-dom";

export default function AllResources(props) {
  const router = useNavigate();
  const { setDevName, setIsVerifiedResource, viewDev, viewingSection } =
    useContext(GlobalVendorContex);
  const [devsCount, setDevsCount] = useState(null);
  const [sorting, setSorting] = useState();
  const [stateData, setStateData] = useState({ skip: 0, take: 20 });
  const [filters, setFilters] = useState({
    status: "all",
    talent: "all",
    hasRates: "all",
    vendor: null,
  });
  // Build the Firestore query reactively from filter + sort state.
  // Server-side filters use `.where()` (status, talent === "talents",
  // vendor). Client-side filters (hasRates, talent === "non-talents")
  // are applied later when the snapshot resolves. orderBy is derived
  // from the current sort state and always falls back to addedOn desc.
  const initialQuery = useMemo(() => {
    const firebaseDirLocal = { ascending: "asc", descending: "desc" };
    let q = props.firebase.db.collection("devs");
    if (filters.status === "active") q = q.where("status", "==", true);
    if (filters.status === "inactive") q = q.where("status", "==", false);
    if (filters.talent === "talents")
      q = q.where("isSpryteTalent", "==", true);
    if (filters.vendor?.vendorID) {
      q = q.where(
        "vendorRef",
        "==",
        docRef(`vendors/${filters.vendor.vendorID}`)
      );
    }
    const sortField =
      sorting?.name && firebaseDirLocal[sorting.direction]
        ? sorting.name === "name"
          ? "first"
          : sorting.name
        : "addedOn";
    const sortDir =
      sorting?.direction && firebaseDirLocal[sorting.direction]
        ? firebaseDirLocal[sorting.direction]
        : "desc";
    return q.orderBy(sortField, sortDir);
  }, [
    props.firebase,
    filters.status,
    filters.talent,
    filters.vendor?.vendorID,
    sorting?.name,
    sorting?.direction,
  ]);
  const [selectedDevsData, setSelectedDevsData] = useState([]);
  const [migrateDevs, setMigrateDevs] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchData, setSearchData] = useState([]);
  const gridRef = useRef({});
  const singleSelectedDev =
    selectedDevsData.length === 1 ? selectedDevsData[0] : null;
  // Memoize objects that Syncfusion compares by reference. Without
  // memoization a new object would be passed on every render, making
  // Syncfusion re-initialise selection / paging / search internals and
  // contributing to the dataStateChange feedback loop the user is hitting.
  const searchOptions = useMemo(
    () => ({
      fields: ["name", "id", "email", "uid"],
      ignoreCase: true,
      operator: "contains",
    }),
    []
  );
  const selectionSettings = useMemo(
    () => ({
      type: "Multiple",
      checkboxOnly: true,
      persistSelection: true,
    }),
    []
  );
  const pageSettings = useMemo(
    () => ({
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
    }),
    []
  );
  const toolbarItems = useMemo(() => {
    if (selectedDevsData.length === 0) return ["Search"];
    if (selectedDevsData.length === 1) {
      return [
        {
          text: "View Dev",
          tooltipText: "View Dev",
          prefixIcon: "e-view",
          id: "view_dev",
        },
        {
          text: "Migrate Dev",
          tooltipText: "Migrate Dev",
          prefixIcon: "e-view",
          id: "migrate_dev",
        },
      ];
    }
    return [
      {
        text: `Migrate Devs (${selectedDevsData.length})`,
        tooltipText: `Migrate ${selectedDevsData.length} developers to a vendor`,
        prefixIcon: "e-view",
        id: "migrate_devs",
      },
    ];
  }, [selectedDevsData.length]);
  // migrate developer state

  useEffect(() => {
    // let sub
    const sub = initialQuery.onSnapshot((snap) => setDevsCount(snap));
    return () => {
      try {
        sub();
      } catch (e) {}
    };
  }, [initialQuery]);

  // Whenever any filter changes, jump back to page 1. Without this the
  // existing `stateData.skip` (e.g. 80 from being on page 5) is reused
  // against the newly-filtered result set, and `query()` falls back to
  // unfiltered docs because `devsCount.docs[skip-1]` doesn't exist in
  // the new filtered set yet — the page jump returns wrong rows.
  // Also explicitly drive Syncfusion's pager back to page 1 so its UI
  // matches our state.
  useEffect(() => {
    setStateData((prev) =>
      prev.skip === 0 ? prev : { ...prev, skip: 0 }
    );
    try {
      gridRef.current?.pagerModule?.goToPage?.(1);
    } catch (e) {}
  }, [
    filters.status,
    filters.talent,
    filters.hasRates,
    filters.vendor?.vendorID,
  ]);

  const query = () => {
    let queryDoc = initialQuery;
    if (stateData.take) {
      queryDoc = queryDoc.limit(stateData?.take);
      if (stateData?.skip > 0 && devsCount?.docs[stateData?.skip - 1]) {
        queryDoc = queryDoc.startAfter(devsCount?.docs[stateData?.skip - 1]);
      }
    }
    return queryDoc;
  };

  useEffect(() => {
    let devsSubs;
    if (
      devsCount?.size &&
      gridRef?.current &&
      !props.isAllDevs &&
      searchValue === "" &&
      searchData.length <= 0
    ) {
      devsSubs = query().onSnapshot(async (snap) => {
        let devs = await props.firebase.allDevsData(snap, true, true);

        // Client-side post-filters that Firestore can't express:
        //   - hasRates: hourlyRate is computed at fetch from the
        //     devs/{id}/rates/{yyyyMMdd} subcollection (no denormalized
        //     field on the parent doc).
        //   - non-talents: `.where("isSpryteTalent", "!=", true)` would
        //     skip docs where the field is missing, so we filter the
        //     fetched page instead so older docs (no field) count as
        //     non-talents.
        if (filters.hasRates === "yes") {
          devs = devs.filter((d) => (d.hourlyRate || 0) > 0);
        } else if (filters.hasRates === "no") {
          devs = devs.filter((d) => !(d.hourlyRate > 0));
        }
        if (filters.talent === "non-talents") {
          devs = devs.filter((d) => d.isSpryteTalent !== true);
        }

        if (gridRef?.current)
          gridRef.current["dataSource"] = {
            count: devsCount.size,
            result: devs,
          };
      });
    }
    return () => {
      try {
        devsSubs();
      } catch (err) {}
    };
  }, [
    stateData?.skip,
    stateData?.take,
    devsCount?.size,
    initialQuery,
    props.isAllDevs,
    searchValue,
    searchData,
    filters.hasRates,
    filters.talent,
  ]);
  /**
   * Called when the grid finished rendering
   * @param {object} args arguments of the grid renderComplete function
   */
  //   const rendereComplete = (args) => {
  //     this.dataStateChange(args);
  //   }

  /**
   * Is trigger when the data source of the grid changes(row deletes or added or edited)
   * @param {*} args
   */
  const dataSourceChanged = async (args) => {
    if (gridRef?.current) {
      if (args?.state?.skip) {
      } else {
      }
    }
  };

  const getDevData = async (searchVal) => {
    const lowerCaseSearchValue = searchVal.toLowerCase(); // converts all search values to lower case
    const initialCapSearchValue =
      lowerCaseSearchValue.charAt(0).toUpperCase() +
      lowerCaseSearchValue.slice(1); // converts first letter to upper case
    const upperCaseSearchValue = searchVal.toUpperCase(); // converts all search values to upper case

    // an array of converted search values which ignores cases irrespective of search inputs
    let convertedSearchValue = [
      initialCapSearchValue,
      lowerCaseSearchValue,
      upperCaseSearchValue,
    ];

    // queries the db and gets the docs/details of the search parameters
    // e.g first name, last name, internal ID, email and uid
    const firstName = colRef("devs")
      .where("first", "in", convertedSearchValue)
      .get();
    const lastName = colRef("devs")
      .where("last", "in", convertedSearchValue)
      .get();
    const devID = docRef(`devs/${searchVal}`).get();
    // email is stored lowercased on the dev doc — exact match against the
    // already-lowercased searchVal
    const byEmail = colRef("devs")
      .where("email", "==", lowerCaseSearchValue)
      .get();
    // uid → dev resolution has two possible paths, since not every dev doc is
    // wired the same way:
    //   1. Newer devs carry a `userRef` field directly on the dev doc — a
    //      Firestore DocumentReference to users/{uid}. Query for it.
    //   2. Older devs link via users/{uid}/accounts/spryte-dev.devRef. Treat
    //      searchVal as a potential uid and dereference that path.
    // Both run in parallel; whichever resolves contributes to devData.
    const byUserRef = colRef("devs")
      .where("userRef", "==", docRef(`users/${searchVal}`))
      .get()
      .catch(() => ({ docs: [] }));
    const byUidDevRef = docRef(`users/${searchVal}/accounts/spryte-dev`)
      .get()
      .then(async (acct) => {
        if (!acct.exists) return null;
        const devRef = acct.data()?.devRef;
        if (!devRef) return null;
        const devDoc = await devRef.get();
        return devDoc.exists ? devDoc : null;
      })
      .catch(() => null);

    // resolves all promises once using Promise.all()
    const results = await Promise.all([
      firstName,
      lastName,
      devID,
      byEmail,
      byUserRef,
      byUidDevRef,
    ]);

    // spreads the details of the resolved promises into the devData array
    let devData = [
      ...results[0].docs,
      ...results[1].docs,
      ...results[3].docs,
      ...results[4].docs,
    ];

    // pushes the resolved internal ID into devData if it exists
    if (results[2].exists) {
      devData.push(results[2]);
    }
    // pushes the uid-resolved dev doc (via users/{uid}/accounts/spryte-dev.devRef)
    if (results[5]) {
      devData.push(results[5]);
    }

    // dedupe by doc.id so a dev matched on multiple fields appears once
    const seen = new Set();
    devData = devData.filter((d) => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });

    // sets the searchData to the array of searched devData values
    // so that only that data would be displayed by allDevsData function
    setSearchData(devData);

    // invokes allDevsData function which returns searched data docs;
    let devs = await props.firebase.allDevsData({ docs: devData }, true, true);

    if (gridRef?.current)
      gridRef.current["dataSource"] = {
        count: devData.length,
        result: devs,
      };
  };

  // Sort state alone drives initialQuery via the useMemo above — this
  // helper now only stores the sort descriptor; the query rebuild is
  // automatic on the next render.
  const getSorting = (sorting) => {
    setSorting(sorting);
  };
  /**
   * triggers when an action happens in the grid(paging ex)
   * @param {*} state
   */
  /**
   * Syncfusion fires this on virtually every grid event — paging, sorting,
   * search, and even after a `dataSource` mutation. Without guards every
   * call would unconditionally `setStateData(newObj)` (always a fresh
   * reference, even when skip/take are unchanged), which re-renders the
   * component, which re-runs the data-loader effect, which re-mutates
   * `gridRef.current.dataSource`, which fires `dataStateChange` again →
   * an infinite feedback loop. Compare values before calling each setter.
   *
   * @param {object} state - Syncfusion grid state (skip, take, search,
   *   sorted, etc.).
   * @returns {Promise<void>}
   */
  const dataStateChange = async (state) => {
    if (!gridRef?.current) return;

    const nextSkip = state.skip ?? 0;
    const nextTake = state.take ?? 20;
    if (nextSkip !== stateData.skip || nextTake !== stateData.take) {
      setStateData({ skip: nextSkip, take: nextTake });
    }

    const nextSearch = state?.search?.[0]?.key ?? "";
    if (nextSearch !== searchValue) {
      setSearchValue(nextSearch);
      if (nextSearch) {
        getDevData(nextSearch);
      } else {
        setSearchData([]);
      }
    }

    const nextSorted = state?.sorted?.[0] ?? null;
    const sortingChanged =
      (nextSorted?.name ?? null) !== (sorting?.name ?? null) ||
      (nextSorted?.direction ?? null) !== (sorting?.direction ?? null);
    if (sortingChanged) {
      setSorting(nextSorted);
    }
  };

  // With selectionSettings.type === "Multiple", Syncfusion fires rowSelected /
  // rowDeselected per row but the full selection is read from the grid in one
  // shot via getSelectedRecords(). Mirror that into selectedDevsData and keep
  // viewDev.current pointing at the single-selected dev for the "View Dev"
  // action that still operates on one resource at a time.
  const onSelectionChange = () => {
    const records = gridRef.current?.getSelectedRecords?.() ?? [];
    setSelectedDevsData(records);
    if (records.length === 1) {
      viewDev.current = records[0];
      viewingSection.current = "Resources";
    }
  };

  const toolbarClick = (args) => {
    const text = args.item.text;
    if (text === "View Dev") {
      if (singleSelectedDev) router(`/resources/${singleSelectedDev.id}`);
      return;
    }
    // "Migrate Dev" (1 selected) and "Migrate Devs (N)" (N > 1) both open
    // the migrate modal; the modal itself adapts to the array length.
    if (text === "Migrate Dev" || text?.startsWith("Migrate Devs")) {
      setMigrateDevs(true);
      return;
    }
  };

  // ==========================================================Code to make the rating and status states work=======================================================================
  // const onQueryCellInfo = (args) => {
  //   // Behavior of status column in dev roster section
  //   if (args.column.field === "status") {
  //     // If status is active give the text a green color and background
  //     if (args.cell.textContent === "Active") {
  //       args.cell.querySelector(".statustxt").classList.add("e-activecolor");
  //       args.cell.querySelector(".statustemp").classList.add("e-activecolor");
  //     }
  //     // If status is inactive give the text a red color and background
  //     if (args.cell.textContent === "Inactive") {
  //       args.cell.querySelector(".statustxt").classList.add("e-inactivecolor");
  //       args.cell.querySelector(".statustemp").classList.add("e-inactivecolor");
  //     }
  //   }
  //   // Behavior of ratings column in dev roster section
  //   if (args.column.field === "rating") {
  //     if (args.column.field === "rating") {
  //       for (let i = 0; i < args.data.rating; i++) {
  //         args.cell.querySelectorAll("span")[i].classList.add("checked");
  //       }
  //     }
  //   }
  // };

  return (
    <div className="all_resources">
      <div className="resource-header requirements_header spmrg_sm-btm">
        <div className="header-intro">
          <h3>All Spryte Resources</h3>
          <div className="desc-icon" style={{ marginTop: "6.5px" }}>
            {/* <FontAwesomeIcon icon={faInfoCircle} className="descIcon" /> */}
            <div className="description">
              Browse and search for Spryte Resources{" "}
            </div>
          </div>
        </div>
      </div>
      <AllResourcesFilterBar
        filters={filters}
        onChange={setFilters}
        firebase={props.firebase}
      />
      <div className="grid-bulk">
        <GridComponent
          id="overviewgrid"
          className="data-grid"
          dataSource={[]}
          ref={gridRef}
          // queryCellInfo={onQueryCellInfo}
          toolbar={toolbarItems}
          searchSettings={searchOptions}
          allowSorting={true}
          width="100%"
          toolbarClick={toolbarClick}
          allowPaging={true}
          allowSelection={true}
          selectionSettings={selectionSettings}
          rowSelected={onSelectionChange}
          rowDeselected={onSelectionChange}
          pageSettings={pageSettings}
          dataSourceChanged={dataSourceChanged}
          dataStateChange={dataStateChange}
          // dataStateChange={dataStateChange}
        >
          <ColumnsDirective>
            <ColumnDirective type="checkbox" width="40"></ColumnDirective>

            <ColumnDirective
              field="company"
              headerText="Partner"
              width="120"
              textAlign="Center"
              allowSorting={false}
              //   validationRules={this.props.validationRules}
            ></ColumnDirective>
            <ColumnDirective
              field="name"
              headerText="First, Last"
              width="120"
              textAlign="Center"
              //   validationRules={this.props.validationRules}
            ></ColumnDirective>
            <ColumnDirective
              field="position"
              headerText="Position"
              width="130"
              editType="dropdownedit"
              textAlign="Center"
              //   edit={this.props.positions}
              //   validationRules={
              //     this.props.isDup
              //       ? this.props.norequired
              //       : this.props.validationRules
              //   }
            />
            <ColumnDirective
              field="status"
              headerText="Profile Status"
              width="100"
              textAlign="Center"
              template={statusTemplate}
              allowSorting={false}
              //   defaultValue={this.props.isDup ? "" : "Active"}
              required={false}
            ></ColumnDirective>
            {/* <ColumnDirective
                  field="rating"
                  headerText="Rating"
                  width="100"
                  textAlign="Center"
                  template={ratingTemplate}
                  // defaultValue={0}
                ></ColumnDirective> */}
            <ColumnDirective
              field="hourlyRate"
              headerText="Hourly Rate"
              width="120"
              format="C2"
              textAlign="Center"
              defaultValue={0}
              allowSorting={false}
            ></ColumnDirective>

            <ColumnDirective
              field="progress"
              required={false}
              headerText="Profile Progress"
              width="100"
              textAlign="Center"
              allowSorting={false}
            ></ColumnDirective>
            <ColumnDirective
              field="locTag"
              headerText="Location"
              width="120"
              textAlign="Center"
              //   validationRules={
              //     this.props.isDup
              //       ? this.props.norequired
              //       : this.props.validationRules
              //   }
            ></ColumnDirective>
            <ColumnDirective
              field="addedOn"
              headerText="Added On"
              width="150"
              textAlign="Center"
              editType="datepickeredit"
              // format="MMMM y"
              format="yMd"
            ></ColumnDirective>
            <ColumnDirective
              field="resume"
              required={false}
              headerText="CV"
              width="100"
              textAlign="Center"
              template={resumeDwload}
              allowSorting={false}
            ></ColumnDirective>
            <ColumnDirective
              field="docs"
              required={false}
              headerText="Docs"
              width="100"
              textAlign="Center"
              template={devDocs}
              allowSorting={false}
            ></ColumnDirective>
            <ColumnDirective
              field="id"
              headerText="Internal ID"
              width="220"
              textAlign="Center"
              allowSorting={false}
              isPrimaryKey={true}
              defaultValue={"0"}
            ></ColumnDirective>
          </ColumnsDirective>

          <Inject
            services={[
              Search,
              Selection,
              Page,
              Toolbar,
              Edit,
              Filter,
              ExcelExport,
              PdfExport,
              Group,
              Sort,
            ]}
          />
        </GridComponent>
      </div>

      {migrateDevs && (
        <>
          <MigrateDevs
            firebase={props.firebase}
            selectedDevsData={selectedDevsData}
            setMigrateDevs={setMigrateDevs}
          />
          <MigrateDevOverlay />
        </>
      )}
    </div>
  );
}
